import { formatDate } from '@angular/common';
import type { Provider } from '@angular/core';
import { type BrnCalendarI18n, provideBrnCalendarI18n } from '@spartan-ng/brain/calendar';
import { provideHlmDatePickerConfig } from '@spartan-ng/helm/date-picker';

const LOCALE = 'it-IT';
const DATE_FORMAT = 'dd/MM/yyyy';

// index 0 is Sunday, as in Date.getDay()
const WEEKDAYS_SHORT = ['Do', 'Lu', 'Ma', 'Me', 'Gi', 'Ve', 'Sa'];
const WEEKDAYS_LONG = ['domenica', 'lunedì', 'martedì', 'mercoledì', 'giovedì', 'venerdì', 'sabato'];
const MONDAY = 1;

const MONTHS_SHORT: ReturnType<BrnCalendarI18n['months']> = [
  'gen', 'feb', 'mar', 'apr', 'mag', 'giu', 'lug', 'ago', 'set', 'ott', 'nov', 'dic'
];

/** Parses `dd/MM/yyyy` (day and month may drop the leading zero). Returns null for text that is not an existing date. */
export function parseItalianDate(value: string): Date | null {
  const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(value.trim());
  if (!match) return null;

  const [day, month, year] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const date = new Date(year, month - 1, day);
  const isRealDate = date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
  return isRealDate ? date : null;
}

/**
 * Italian defaults for the spartan components that are not localized by the LOCALE_ID: the calendar labels, the
 * first day of the week and the date format shown by `hlm-date-picker`. Register it once in app.config.ts and in the
 * shared test render helper so the specs see what the users see.
 */
export function provideSpartanItalian(): Provider[] {
  return [
    provideBrnCalendarI18n({
      formatWeekdayName: index => WEEKDAYS_SHORT[index],
      labelWeekday: index => WEEKDAYS_LONG[index],
      firstDayOfWeek: () => MONDAY,
      months: () => MONTHS_SHORT,
      formatHeader: (month, year) => new Date(year, month).toLocaleDateString(LOCALE, { month: 'long', year: 'numeric' }),
      formatMonth: month => new Date(2000, month).toLocaleDateString(LOCALE, { month: 'short' }),
      formatYear: year => new Date(year, 0).toLocaleDateString(LOCALE, { year: 'numeric' }),
      labelPrevious: () => 'Vai al mese precedente',
      labelNext: () => 'Vai al mese successivo'
    }),
    provideHlmDatePickerConfig<Date>({
      formatDate: date => formatDate(date, DATE_FORMAT, LOCALE),
      formatInputDate: date => formatDate(date, DATE_FORMAT, LOCALE),
      parseDate: parseItalianDate
    })
  ];
}
